"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "react-router-dom"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Mail, Twitter, Send, Book } from 'lucide-react'
import { useToast } from "@/components/ui/use-toast"
import { api } from "../../lib/convex"
import { useAction } from "convex/react"
import { isValidEmail } from "@/lib/email"

function Footerdemo() {
  const [isChatOpen, setIsChatOpen] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const { toast } = useToast()
  const subscribeToNewsletter = useAction(api.email.subscribeToNewsletter)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }
    
    if (!isValidEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await subscribeToNewsletter({
        email: email,
      });
      
      if (result.success) {
        toast({
          title: "Success!",
          description: "You've been subscribed to our newsletter.",
        });
        setEmail('');
      } else {
        toast({
          title: "Subscription failed",
          description: result.error || "An error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Subscription failed",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative border-t bg-cream text-foreground transition-colors duration-300">
      <div className="container mx-auto px-4 py-12 md:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex flex-col">
                <span className="text-xl font-bold">AiSEOBlogGenerator</span>
                <span className="text-sm text-gray-600">A content writer that doesn't suck</span>
              </div>
            </div>
            <p className="mb-6 text-gray-700">
              Stop wrestling with AI prompts. Start creating content that converts.
            </p>
            <form className="relative" onSubmit={handleSubmit}>
              <Input
                type="email"
                placeholder="Enter your email"
                className="pr-12 bg-white border-gray-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
              <Button
                type="submit"
                size="icon"
                className="absolute right-1 top-1 h-8 w-8 rounded-full bg-black text-white transition-transform hover:scale-105"
                disabled={isSubmitting}
              >
                <Send className="h-4 w-4" />
                <span className="sr-only">Subscribe</span>
              </Button>
            </form>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold">Quick Links</h3>
            <nav className="space-y-2 text-sm">
              <Link to="/" className="block transition-colors hover:text-dogblue">
                Home
              </Link>
              <Link to="/writer" className="block transition-colors hover:text-dogblue">
                Content Writer
              </Link>
              <Link to="/posts" className="block transition-colors hover:text-dogblue">
                My Posts
              </Link>
              <Link to="/docs" className="block transition-colors hover:text-dogblue">
                <span className="flex items-center gap-1.5">
                  <Book className="w-3.5 h-3.5" />
                  Documentation
                </span>
              </Link>
            </nav>
          </div>
          <div>
            <h3 className="mb-4 text-lg font-semibold">Contact Us</h3>
            <address className="space-y-2 text-sm not-italic">
              <p>Email: support@aiseobloggenerator.com</p>
              <p>Twitter: @makershelpdesk</p>
              <Link to="/contact" className="block text-dogblue hover:underline">
                Send us a message →
              </Link>
            </address>
          </div>
          <div className="relative">
            <h3 className="mb-4 text-lg font-semibold">Follow Us</h3>
            <div className="mb-6 flex space-x-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full">
                      <Twitter className="h-4 w-4" />
                      <span className="sr-only">Twitter</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Follow us on Twitter</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="mt-4">
              <a href="https://makershelpdesk.com" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://emp3.b-cdn.net/circle%20gabe.png" 
                  alt="Makers Help Desk" 
                  className="w-16 h-16 object-cover"
                />
              </a>
            </div>
          </div>
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t pt-8 text-center md:flex-row">
          <p className="text-sm text-muted-foreground">
            © 2024 AiSEOBlogGenerator. All rights reserved.
          </p>
          <nav className="flex gap-4 text-sm">
            <Link to="/privacy" className="transition-colors hover:text-dogblue">
              Privacy Policy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-dogblue">
              Terms of Service
            </Link>
            <Link to="/cookie-settings" className="transition-colors hover:text-dogblue">
              Cookie Settings
            </Link>
            <a href="https://convex.link/gabe" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-dogblue">
              Powered by Convex
            </a>
          </nav>
        </div>
      </div>
    </footer>
  )
}

export { Footerdemo }